import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/axios';

const Auth = () => {
    // --- STATES ---
    const [isLogin, setIsLogin] = useState(true);
    const [isForgotPassword, setIsForgotPassword] = useState(false); // NEW: Toggles the Forgot Password view
    const [regSuccess, setRegSuccess] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false); // NEW: Shows success after requesting reset
    
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
    const [error, setError] = useState('');
    
    
    const [searchParams] = useSearchParams();
    const isVerified = searchParams.get('verified') === 'true';

    // Clear the error message when toggling between views
    useEffect(() => {
        setError('');
    }, [isLogin, isForgotPassword]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // --- STANDARD LOGIN / REGISTER HANDLER ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (!isLogin && formData.password !== formData.confirmPassword) {
            return setError('Passwords do not match!');
        }

        setIsLoading(true);
        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const payload = isLogin 
                ? { username: formData.username, password: formData.password } 
                : { username: formData.username, email: formData.email, password: formData.password };

            const { data } = await api.post(endpoint, payload);
            
            if (isLogin) {
                localStorage.setItem('token', data.token);
                localStorage.setItem('user', JSON.stringify(data.user));
                window.location.href = '/dashboard'; 
            } else {
                setRegSuccess(true);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.response?.data?.message || 'Authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    // --- NEW: FORGOT PASSWORD HANDLER ---
    const handleForgotPasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        
        try {
            // Sends the email from the form to our new backend route
            await api.post('/auth/forgot-password', { email: formData.email });
            setResetEmailSent(true);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send reset link. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- RESET VIEW HELPER ---
    const backToLogin = () => {
        setIsForgotPassword(false);
        setResetEmailSent(false);
        setRegSuccess(false);
        setIsLogin(true);
        setFormData({ username: '', email: '', password: '', confirmPassword: '' });
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 transition-all duration-500">
                
                {regSuccess ? (
                    /* --- REGISTRATION SUCCESS UI --- */
                    <div className="text-center py-4 animate-in fade-in zoom-in duration-300">
                        <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Check Your Email!</h2>
                        <p className="text-sm font-medium text-gray-500 mt-2 mb-8">
                            We've sent a verification link to your email address. Please click it to activate your account before logging in.
                        </p>
                        <button onClick={backToLogin} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-black text-sm uppercase tracking-wide shadow-lg shadow-blue-100 transition-all active:scale-95">
                            Back to Login
                        </button>
                    </div>

                ) : isForgotPassword ? (
                    /* --- FORGOT PASSWORD UI --- */
                    <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                        {resetEmailSent ? (
                            <div className="text-center py-4">
                                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h2 className="text-2xl font-black text-gray-900 tracking-tight">Link Sent!</h2>
                                <p className="text-sm font-medium text-gray-500 mt-2 mb-8">
                                    If an account exists for <b>{formData.email}</b>, we have sent password reset instructions to it.
                                </p>
                                <button onClick={backToLogin} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-black text-sm uppercase tracking-wide shadow-lg shadow-blue-100 transition-all active:scale-95">
                                    Return to Login
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="text-center mb-8">
                                    <h2 className="text-3xl font-black text-gray-900 tracking-tight">Reset Password</h2>
                                    <p className="text-sm font-medium text-gray-500 mt-2">
                                        Enter your email address and we'll send you a link to reset your password.
                                    </p>
                                </div>
                                
                                {error && (
                                    <div className="mb-6 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm font-bold text-center animate-in fade-in zoom-in">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleForgotPasswordSubmit} className="space-y-5">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Email Address</label>
                                        <input 
                                            type="email" name="email" placeholder="you@example.com" 
                                            value={formData.email} onChange={handleChange} required 
                                            className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 font-medium transition-all"
                                        />
                                    </div>
                                    <button 
                                        type="submit" disabled={isLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-black text-sm uppercase tracking-wide shadow-lg shadow-blue-100 transition-all active:scale-95 disabled:opacity-70 mt-4"
                                    >
                                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </form>
                                <div className="mt-6 text-center">
                                    <button onClick={backToLogin} className="text-sm font-bold text-gray-500 hover:text-gray-800 transition-colors">
                                        ← Back to login
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    /* --- LOGIN / REGISTER FORM (Existing) --- */
                    <>
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-black text-gray-900 tracking-tight">
                                {isLogin ? 'Welcome Back' : 'Create Account'}
                            </h2>
                            <p className="text-sm font-medium text-gray-500 mt-2">
                                {isLogin ? 'Enter your details to access your tournaments.' : 'Sign up to start organizing tournaments today.'}
                            </p>
                        </div>

                        {isVerified && isLogin && !error && (
                            <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm font-bold text-center animate-in fade-in slide-in-from-top-2">
                                Email successfully verified! You can now log in. 🎉
                            </div>
                        )}

                        {error && (
                            <div className="mb-6 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm font-bold text-center animate-in fade-in zoom-in">
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Username / Email</label>
                                <input 
                                    type="text" name="username" placeholder="TourneyMaster" 
                                    value={formData.username} onChange={handleChange} required 
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 font-medium transition-all"
                                />
                            </div>

                            {!isLogin && (
                                <div className="animate-in fade-in slide-in-from-top-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Email Address</label>
                                    <input 
                                        type="email" name="email" placeholder="you@example.com" 
                                        value={formData.email} onChange={handleChange} required={!isLogin} 
                                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 font-medium transition-all"
                                    />
                                </div>
                            )}

                            <div>
                                <div className="flex justify-between items-center mb-2 ml-1 mr-1">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">Password</label>
                                    
                                    {/* NEW: FORGOT PASSWORD LINK */}
                                    {isLogin && (
                                        <button 
                                            type="button" 
                                            onClick={() => { setIsForgotPassword(true); setError(''); }}
                                            className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            Forgot password?
                                        </button>
                                    )}
                                </div>
                                <input 
                                    type="password" name="password" placeholder="••••••••" 
                                    value={formData.password} onChange={handleChange} required 
                                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 font-medium transition-all"
                                />
                            </div>

                            {!isLogin && (
                                <div className="animate-in fade-in slide-in-from-top-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">Confirm Password</label>
                                    <input 
                                        type="password" name="confirmPassword" placeholder="••••••••" 
                                        value={formData.confirmPassword} onChange={handleChange} required={!isLogin} 
                                        className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none text-gray-800 font-medium transition-all"
                                    />
                                </div>
                            )}

                            <button 
                                type="submit" disabled={isLoading}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-black text-sm uppercase tracking-wide shadow-lg shadow-blue-100 transition-all active:scale-95 disabled:opacity-70 mt-4"
                            >
                                {isLoading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
                            </button>
                        </form>

                        <div className="mt-8 text-center">
                            <p className="text-sm font-medium text-gray-500">
                                {isLogin ? "Don't have an account?" : "Already have an account?"}
                                <button 
                                    type="button"
                                    onClick={() => { setIsLogin(!isLogin); setError(''); }}
                                    className="ml-2 font-black text-blue-600 hover:text-blue-800 transition-colors"
                                >
                                    {isLogin ? 'Sign Up' : 'Log In'}
                                </button>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Auth;